/**
 * Step 4 — Quiz Count (Phase 2 task #6).
 *
 * Per plan §6.1 task #6 + Doc 04 §8:
 *  - Slider 1-5 worksheet/hari (custom 5-tap zones, no external dep)
 *  - Daily count 1-5, with no weekly-frequency choice
 *  - Default: count=3
 *  - CTA "Lanjut" always enabled (default value is valid)
 *  - Wire to API: POST /v1/quiz/answer (questionKey='count',
 *    questionOrder=3, payload includes count)
 *  - "Lewati" → write default count=3
 *    with skipped=true, then navigate to /summary
 *
 * Design notes:
 *  - Custom slider: 5 evenly-spaced tap targets over a horizontal track.
 *    Each tap maps to (count-1)*100/4 % of the way, and the thumb
 *    visualizes at the corresponding position. This avoids pulling in
 *    @react-native-community/slider for a 5-value discrete control.
 *  - Single source of truth is `lib/preferences-defaults.ts` for the
 *    COUNT_MIN/MAX/DEFAULT slider bounds.
 *  - The API still receives the fixed legacy frequency value 'daily'.
 *  - currentChild is required for the POST. Defensive redirect to
 *    /child-profile if missing.
 *  - timeSpentMs measured from mount to submit, capped at 10 min to
 *    match server zod cap.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import {
  LayoutChangeEvent,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { OnboardingStepShell } from '@/components/onboarding/OnboardingStepShell';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { ApiError } from '@/lib/api/client';
import { submitQuizAnswer } from '@/lib/api/quiz';
import { trackAnalyticsEvent } from '@/lib/analytics';
import { useOnboarding } from '@/contexts/onboarding-context';
import {
  COUNT_MAX,
  COUNT_MIN,
  COUNT_DEFAULT_SKIP,
  DEFAULT_COUNT,
} from '@/lib/preferences-defaults';
import type { DailyCount } from '@funpaper/shared';

import { ONBOARDING_ROUTES, getStepByPath } from '@/constants/onboarding';

const TIME_SPENT_CAP_MS = 600_000; // matches server zod cap

export default function QuizCountScreen() {
  const router = useRouter();
  const step = getStepByPath(ONBOARDING_ROUTES.quizCount);
  const { currentChild } = useOnboarding();

  const [count, setCount] = useState<DailyCount>(DEFAULT_COUNT);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track width in px (measured via onLayout) — used for the thumb
  // position calculation. 0 until first layout.
  const [trackWidth, setTrackWidth] = useState<number>(0);
  const onTrackLayout = useCallback((e: LayoutChangeEvent) => {
    setTrackWidth(e.nativeEvent.layout.width);
  }, []);

  const mountTimeRef = useRef<number>(Date.now());

  // Defensive redirect: this screen needs the child profile from step 2.
  useEffect(() => {
    if (!currentChild) {
      router.replace(ONBOARDING_ROUTES.childProfile);
    }
  }, [currentChild, router]);

  const onCountChange = useCallback((next: DailyCount) => {
    setCount(next);
    setError(null);
  }, []);

  // Slider geometry: 5 evenly-spaced positions.
  // Position 0 (count=1) at left edge, position 4 (count=5) at right edge.
  // Thumb is centered on the active tick — its center is at
  //   (count - 1) / (COUNT_MAX - COUNT_MIN) * trackWidth
  const thumbLeftPx =
    trackWidth > 0
      ? ((count - COUNT_MIN) / (COUNT_MAX - COUNT_MIN)) * trackWidth
      : 0;
  // Fill width: from left edge to the center of the active tick.
  const fillWidthPx = thumbLeftPx;
  // Tick mark positions (centered on each value).
  const tickPositions = Array.from(
    { length: COUNT_MAX - COUNT_MIN + 1 },
    (_, i) => (i / (COUNT_MAX - COUNT_MIN)) * (trackWidth > 0 ? trackWidth : 0),
  );

  const submit = useCallback(
    async (override?: DailyCount) => {
      if (isSubmitting) return;
      if (!currentChild) return; // guarded by useEffect

      const finalCount = override ?? count;
      const timeSpentMs = Math.min(
        Math.max(0, Date.now() - mountTimeRef.current),
        TIME_SPENT_CAP_MS,
      );

      setIsSubmitting(true);
      setError(null);
      try {
        await submitQuizAnswer({
          childProfileId: currentChild.id,
          questionKey: 'count',
          questionOrder: 3,
          answerPayload: {
            selected: [], // not used for count question
            skipped: override !== undefined,
            timeSpentMs,
            count: finalCount,
            frequency: 'daily',
          },
        });
        trackAnalyticsEvent('Onboarding Step Completed', {
          step_name: 'daily_count',
          skipped: override !== undefined,
          daily_count: finalCount,
        });
        router.push(ONBOARDING_ROUTES.summary);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : 'Gagal menyimpan preferensi worksheet. Coba lagi.';
        setError(message);
        setIsSubmitting(false);
      }
    },
    [count, currentChild, isSubmitting, router],
  );

  const onKembali = () => router.back();
  const onLewati = () => submit(COUNT_DEFAULT_SKIP);
  const onLanjut = () => submit();

  if (!currentChild) {
    return null;
  }

  return (
    <OnboardingStepShell
      progress={step?.progress ?? 4 / 5}
      stepLabel={step?.stepLabel ?? '4 / 5'}
      onKembali={onKembali}
      onLewati={onLewati}
      onLanjut={onLanjut}
      lanjutDisabled={isSubmitting}
      lanjutLabel={isSubmitting ? 'Menyimpan…' : 'Lanjut'}
    >
      <View style={styles.headerBlock}>
        <ThemedText type="subtitle" style={styles.title}>
          Berapa worksheet per hari?
        </ThemedText>
        <ThemedText
          type="default"
          themeColor="textSecondary"
          style={styles.subtitle}
        >
          Bisa diubah kapan aja di{' '}
          <ThemedText type="smallBold" themeColor="textSecondary">
            Preferensi
          </ThemedText>
          .
        </ThemedText>
      </View>

      <View style={styles.controlsWrap}>
        {/* Big live number */}
        <ThemedText type="subtitle" style={styles.bigCount}>
          {count} worksheet per hari
        </ThemedText>

        {/* Slider */}
        <View style={styles.sliderSection}>
          <View style={styles.sliderTrack} onLayout={onTrackLayout}>
            <View
              style={[
                styles.sliderFill,
                { width: fillWidthPx > 0 ? fillWidthPx : 0 },
              ]}
            />
            {tickPositions.map((cx, i) => {
              const value = (COUNT_MIN + i) as DailyCount;
              const active = value === count;
              return (
                <Pressable
                  key={value}
                  onPress={() => onCountChange(value)}
                  disabled={isSubmitting}
                  accessibilityRole="adjustable"
                  accessibilityLabel={`${value} worksheet per hari`}
                  accessibilityValue={{ min: COUNT_MIN, max: COUNT_MAX, now: value }}
                  hitSlop={12}
                  style={[
                    styles.tickHit,
                    { left: cx - 24 },
                  ]}
                >
                  <View
                    style={[
                      styles.tickMark,
                      active && styles.tickMarkActive,
                    ]}
                  />
                </Pressable>
              );
            })}
            <View
              pointerEvents="none"
              style={[
                styles.thumb,
                {
                  left:
                    thumbLeftPx > 0
                      ? thumbLeftPx - styles.thumb.width / 2
                      : -styles.thumb.width / 2,
                },
              ]}
            />
          </View>
          <View style={styles.tickLabels}>
            {[1, 2, 3, 4, 5].map((n) => (
              <ThemedText
                key={n}
                type="small"
                style={styles.tickLabel}
              >
                {n}
              </ThemedText>
            ))}
          </View>
        </View>

        {error ? (
          <View style={styles.errorBox} accessibilityRole="alert">
            <ThemedText type="small" style={styles.errorText}>
              {error}
            </ThemedText>
          </View>
        ) : null}
      </View>
    </OnboardingStepShell>
  );
}

const TRACK_HEIGHT = 4;
const TICK_SIZE = 12;
const THUMB_SIZE = 24;

const styles = StyleSheet.create({
  headerBlock: {
    gap: Spacing.two,
    paddingTop: Spacing.two,
  },
  title: {
    color: Brand.primary,
  },
  subtitle: {
    lineHeight: 22,
  },
  controlsWrap: {
    flex: 1,
    paddingTop: Spacing.four,
    gap: Spacing.four,
  },
  bigCount: {
    color: Brand.primary,
    textAlign: 'center',
  },
  sliderSection: {
    paddingTop: Spacing.three,
    gap: Spacing.two,
  },
  sliderTrack: {
    height: THUMB_SIZE,
    justifyContent: 'center',
    position: 'relative',
    marginHorizontal: THUMB_SIZE / 2, // leave room for the thumb at the ends
  },
  sliderFill: {
    position: 'absolute',
    left: 0,
    height: TRACK_HEIGHT,
    backgroundColor: Brand.primary,
    borderRadius: TRACK_HEIGHT / 2,
    top: (THUMB_SIZE - TRACK_HEIGHT) / 2,
  },
  tickHit: {
    position: 'absolute',
    top: 0,
    width: 48,
    height: THUMB_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tickMark: {
    width: TICK_SIZE,
    height: TICK_SIZE,
    borderRadius: TICK_SIZE / 2,
    backgroundColor: '#d1d1d1',
  },
  tickMarkActive: {
    backgroundColor: Brand.primary,
  },
  thumb: {
    position: 'absolute',
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    backgroundColor: Brand.primary,
    top: 0,
    borderWidth: 3,
    borderColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
  },
  tickLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: THUMB_SIZE / 2,
    paddingTop: Spacing.one,
  },
  tickLabel: {
    color: '#6b6573',
    fontVariant: ['tabular-nums'],
  },
  errorBox: {
    backgroundColor: 'rgba(229, 62, 62, 0.08)',
    borderColor: 'rgba(229, 62, 62, 0.3)',
    borderWidth: 1,
    borderRadius: Radii.medium,
    paddingVertical: Spacing.two,
    paddingHorizontal: Spacing.three,
  },
  errorText: {
    color: Brand.danger,
    fontWeight: '500',
  },
});
